import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { UserBadge } from '../components/UserBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Plus, Loader2, GripVertical, Euro, Target, LayoutGrid, List, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const STAGES = [
  { key: 'new_lead', label: 'Nuovo Lead', color: '#6366f1', prob: 10 },
  { key: 'qualified', label: 'Qualificato', color: '#3b82f6', prob: 25 },
  { key: 'proposal', label: 'Proposta Inviata', color: '#f59e0b', prob: 50 },
  { key: 'negotiation', label: 'Trattativa', color: '#f97316', prob: 75 },
  { key: 'won', label: 'Vinto', color: '#22c55e', prob: 100 },
  { key: 'lost', label: 'Perso', color: '#ef4444', prob: 0 },
];

function formatEuro(v) {
  if (!v) return '€0';
  if (v >= 1000000) return `€${(v/1000000).toFixed(1)}M`;
  if (v >= 1000) return `€${(v/1000).toFixed(0)}k`;
  return `€${v.toLocaleString('it-IT')}`;
}

function OpportunityCard({ opp, onDragStart, onDelete }) {
  const { canDelete } = usePermissions();
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, opp._id || opp.id)}
      data-testid={`opp-card-${opp._id || opp.id}`}
      className="bg-card border border-border rounded-lg p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-all duration-150 group"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium leading-snug flex-1">{opp.title}</p>
        {canDelete('pipeline') && (
          <Button
            variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 flex-shrink-0 text-destructive"
            onClick={() => onDelete(opp._id || opp.id, opp.title)}
          ><Trash2 size={11} /></Button>
        )}
      </div>
      {opp.company_name && <p className="text-xs text-muted-foreground mt-1">{opp.company_name}</p>}
      {opp.assigned_to && <div className="mt-1.5"><UserBadge userId={opp.assigned_to} size="xs" /></div>}
      <div className="flex items-center justify-between mt-2">
        <span className="text-sm font-semibold text-primary">{formatEuro(opp.value)}</span>
        <span className="text-xs text-muted-foreground">{opp.probability}%</span>
      </div>
      {opp.expected_close && (
        <p className="text-xs text-muted-foreground mt-1">
          Chiusura: {new Date(opp.expected_close).toLocaleDateString('it-IT')}
        </p>
      )}
    </div>
  );
}

function KanbanColumn({ stage, opportunities, onDragStart, onDragOver, onDrop, onDelete }) {
  const totalValue = opportunities.reduce((s, o) => s + (o.value || 0), 0);
  return (
    <div
      className="flex flex-col bg-muted/30 rounded-xl min-w-[240px] w-64"
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, stage.key)}
    >
      <div className="p-3 border-b border-border">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ background: stage.color }} />
            <span className="text-sm font-semibold" style={{fontFamily:'Manrope, sans-serif'}}>{stage.label}</span>
          </div>
          <Badge variant="outline" className="text-xs">{opportunities.length}</Badge>
        </div>
        <p className="text-xs text-muted-foreground">{formatEuro(totalValue)}</p>
      </div>
      <div className="flex-1 p-2 space-y-2 overflow-y-auto" style={{ maxHeight: '60vh' }}>
        {opportunities.map(opp => (
          <OpportunityCard key={opp._id || opp.id} opp={opp} onDragStart={onDragStart} onDelete={onDelete} />
        ))}
        {opportunities.length === 0 && (
          <div className="text-center py-6 text-muted-foreground text-xs">Trascina qui</div>
        )}
      </div>
    </div>
  );
}

export default function Pipeline() {
  const { canCreate, canDelete } = usePermissions();
  const [opportunities, setOpportunities] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [form, setForm] = useState({ title:'', value:'', stage:'new_lead', company_id:'', expected_close:'', notes:'' });

  const fetchOpps = async () => {
    try {
      const [oppsRes, compRes] = await Promise.all([
        axios.get(`${API}/opportunities`),
        axios.get(`${API}/companies`),
      ]);
      setOpportunities(oppsRes.data);
      setCompanies(compRes.data);
    } catch { toast.error('Errore caricamento'); } finally { setLoading(false); }
  };

  useEffect(() => { fetchOpps(); }, []);

  const handleDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.setData('text/plain', id);
  };

  const handleDragOver = (e) => { e.preventDefault(); };

  const handleDrop = async (e, stage) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain') || dragId;
    if (!id) return;
    try {
      await axios.patch(`${API}/opportunities/${id}/stage`, { stage });
      setOpportunities(prev => prev.map(o =>
        (o._id === id || o.id === id)
          ? { ...o, stage, probability: STAGES.find(s => s.key === stage)?.prob || o.probability }
          : o
      ));
    } catch { toast.error('Errore aggiornamento stage'); }
    setDragId(null);
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Eliminare "${title}"?`)) return;
    try {
      await axios.delete(`${API}/opportunities/${id}`);
      toast.success('Opportunità eliminata');
      setOpportunities(prev => prev.filter(o => o._id !== id && o.id !== id));
    } catch { toast.error('Errore'); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, value: parseFloat(form.value) || 0 };
      if (!payload.company_id) delete payload.company_id;
      if (!payload.expected_close) delete payload.expected_close;
      await axios.post(`${API}/opportunities`, payload);
      toast.success('Opportunità creata');
      setShowForm(false);
      setForm({ title:'', value:'', stage:'new_lead', company_id:'', expected_close:'', notes:'' });
      fetchOpps();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); } finally { setSaving(false); }
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const totalPipeline = opportunities.filter(o => !['won','lost'].includes(o.stage)).reduce((s, o) => s + (o.weighted_value || 0), 0);
  const wonValue = opportunities.filter(o => o.stage === 'won').reduce((s, o) => s + (o.value || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{fontFamily:'Manrope, sans-serif'}}>Pipeline Commerciale</h1>
          <p className="text-muted-foreground text-sm mt-1">{opportunities.length} opportunità totali</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-border rounded-lg overflow-hidden">
            <Button variant={view === 'kanban' ? 'default' : 'ghost'} size="sm" className="rounded-none" onClick={() => setView('kanban')} data-testid="kanban-view-btn">
              <LayoutGrid size={14} className="mr-1.5" />Kanban
            </Button>
            <Button variant={view === 'list' ? 'default' : 'ghost'} size="sm" className="rounded-none" onClick={() => setView('list')} data-testid="list-view-btn">
              <List size={14} className="mr-1.5" />Lista
            </Button>
          </div>
          {canCreate('pipeline') && (
            <Button onClick={() => setShowForm(true)} data-testid="add-opportunity-btn">
              <Plus size={16} className="mr-2" />Nuova Opportunità
            </Button>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Pipeline Attiva', value: formatEuro(totalPipeline), icon: Target },
          { label: 'Valore Vinto', value: formatEuro(wonValue), icon: Euro },
          { label: 'Opportunità Aperte', value: opportunities.filter(o => !['won','lost'].includes(o.stage)).length, icon: LayoutGrid },
          { label: 'Totale', value: opportunities.length, icon: List },
        ].map(({ label, value, icon: Icon }) => (
          <Card key={label}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon size={16} className="text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="font-bold text-sm" style={{fontFamily:'Manrope, sans-serif'}}>{value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : view === 'kanban' ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {STAGES.map(stage => (
            <KanbanColumn
              key={stage.key}
              stage={stage}
              opportunities={opportunities.filter(o => o.stage === stage.key)}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDelete={handleDelete}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Titolo</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Azienda</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Fase</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Valore</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">Chiusura</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {opportunities.map(opp => {
                const id = opp._id || opp.id;
                const stage = STAGES.find(s => s.key === opp.stage);
                return (
                  <tr key={id} className="border-b hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 font-medium">{opp.title}</td>
                    <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{opp.company_name || '-'}</td>
                    <td className="px-4 py-3 hidden md:table-cell"><UserBadge userId={opp.assigned_to} size="xs" /></td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full font-medium"
                        style={{ background: `${stage?.color}20`, color: stage?.color }}>
                        {stage?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold">{formatEuro(opp.value)}</td>
                    <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell">
                      {opp.expected_close ? new Date(opp.expected_close).toLocaleDateString('it-IT') : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canDelete('pipeline') && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(id, opp.title)}>
                          <Trash2 size={13} />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{fontFamily:'Manrope, sans-serif'}}>Nuova Opportunità</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label>Titolo *</Label>
              <Input value={form.title} onChange={e => setField('title', e.target.value)} required data-testid="opp-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Valore (€)</Label>
                <Input type="number" value={form.value} onChange={e => setField('value', e.target.value)} placeholder="0" data-testid="opp-value-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Fase</Label>
                <Select value={form.stage} onValueChange={v => setField('stage', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Azienda</Label>
              <Select value={form.company_id} onValueChange={v => setField('company_id', v)}>
                <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>{companies.map(c => <SelectItem key={c._id||c.id} value={c._id||c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Data chiusura prevista</Label>
              <Input type="date" value={form.expected_close} onChange={e => setField('expected_close', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Note</Label>
              <Textarea value={form.notes} onChange={e => setField('notes', e.target.value)} rows={2} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowForm(false)}>Annulla</Button>
              <Button type="submit" className="flex-1" disabled={saving} data-testid="save-opportunity-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
